import { ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  useCommissionDeal,
  useCommissionSurgery,
} from '../../shared/api/generated/commercial-commissions/commercial-commissions'
import type { CommissionLine } from '../../shared/api/generated/model'
import { Drawer } from '../../shared/ui/Drawer'
import { AttributionForm } from './AttributionForm'
import {
  errorMessage,
  issueLabels,
  money,
  participationLabels,
  percent,
  shortDate,
  statusMeta,
  triggerLabels,
} from './helpers'

/**
 * Painel lateral de uma parcela: valores, links, atribuição e correção.
 * Concentra o que antes ficava espalhado pela linha da tabela.
 */
export function CommissionDrawer({
  line,
  canReview,
  onClose,
  onSaved,
}: {
  line: CommissionLine
  canReview: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const deal = useCommissionDeal(line.proposal_id ?? 0, {
    query: { enabled: !!line.proposal_id, retry: false },
  })
  const surgery = useCommissionSurgery(line.surgery_id, {
    query: { enabled: !line.proposal_id, retry: false },
  })
  const ctx = line.proposal_id ? deal : surgery
  const attribution = ctx.data?.attributions[0]
  const status = statusMeta[line.status] ?? { label: line.status, tone: 'neutral' as const }
  const issues = (line.issues ?? []).map((k) => issueLabels[k] ?? k)
  return (
    <Drawer
      open
      onClose={onClose}
      width={520}
      title={
        <div className="drawer-title-block">
          <div className="drawer-title">{line.patient_name}</div>
          <div className="drawer-subtitle">
            {triggerLabels[line.trigger] ?? line.trigger}
            <span className="drawer-subtitle-sep">·</span>
            {shortDate(line.source_date)}
            <span className="drawer-subtitle-sep">·</span>
            <span className={`cc-status is-${status.tone}`}>{status.label}</span>
          </div>
        </div>
      }
    >
      <div className="cc-drawer">
        <dl className="cc-facts">
          <div>
            <dt>Comercial</dt>
            <dd>
              {line.seller_name ?? <span className="cc-undefined">A definir</span>}
              {line.participation && (
                <span className="cc-dim"> · {participationLabels[line.participation]}</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Honorários</dt>
            <dd className="cc-num">{money(line.medical_fees_base_cents)}</dd>
          </div>
          <div>
            <dt>Taxa</dt>
            <dd className="cc-num">{percent(line.rate_bps)}</dd>
          </div>
          <div>
            <dt>Comissão</dt>
            <dd className="cc-num cc-amount">{money(line.commission_amount_cents)}</dd>
          </div>
        </dl>

        {issues.length > 0 && (
          <ul className="cc-issues" aria-label="Pendências">
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        )}

        <div className="cc-links">
          <Link to={`/cirurgias/${line.surgery_id}`}>
            Cirurgia #{line.surgery_id} <ArrowUpRight size={13} aria-hidden="true" />
          </Link>
          {line.proposal_id && (
            <Link to={`/deals/${line.proposal_id}`}>
              Deal #{line.proposal_id} <ArrowUpRight size={13} aria-hidden="true" />
            </Link>
          )}
        </div>

        <section className="cc-drawer-section">
          <h3>Atribuição</h3>
          {ctx.isLoading && <p className="cc-muted">Carregando atribuição…</p>}
          {ctx.isError && (
            <p className="cc-notice cc-error" role="alert">
              {errorMessage(ctx.error)}
            </p>
          )}
          {attribution && (
            <>
              <dl className="cc-facts">
                <div>
                  <dt>Originador</dt>
                  <dd>
                    {attribution.originator_name ?? (
                      <span className="cc-undefined">Não identificado</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Fechador</dt>
                  <dd>
                    {attribution.closer_name ?? (
                      <span className="cc-undefined">Não identificado</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Data da venda</dt>
                  <dd>{shortDate(attribution.closed_at)}</dd>
                </div>
                <div>
                  <dt>Origem</dt>
                  <dd>
                    {attribution.source === 'manual'
                      ? 'Informada manualmente'
                      : 'Recuperada do histórico'}
                  </dd>
                </div>
              </dl>
              {!!attribution.history_json?.length && (
                <details className="cc-history">
                  <summary>Histórico de correções ({attribution.history_json.length})</summary>
                  <ul className="cc-history-list">
                    {attribution.history_json.map((change, index) => (
                      <li key={index}>
                        <span>
                          {shortDate(String(change.at))} · usuário #{String(change.actor_id)}
                        </span>
                        {String(change.reason)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {ctx.data && ctx.data.attributions.length > 1 && (
                <p className="cc-muted">
                  Este deal tem {ctx.data.attributions.length} atribuições (aditivos). A correção
                  abaixo vale para a primeira.
                </p>
              )}
            </>
          )}
        </section>

        {canReview && attribution && (
          <section className="cc-drawer-section">
            <h3>Corrigir atribuição</h3>
            <p className="cc-muted">
              {attribution.proposal_id
                ? `Vale para todas as cirurgias do deal #${attribution.proposal_id}.`
                : 'Cirurgia histórica sem deal vinculado.'}
            </p>
            <AttributionForm
              key={attribution.revision}
              attribution={attribution}
              onSaved={onSaved}
              idPrefix="cc-drawer-attr"
            />
          </section>
        )}
      </div>
    </Drawer>
  )
}
